import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { heroMovies, movies, categories } from '@/mocks/movies';
import type { MovieItem } from '@/mocks/movies';
import MovieCard from '@/components/base/MovieCard';

export default function HeroCarousel() {
  const [current, setCurrent] = useState(0);

  const next = useCallback(() => {
    setCurrent((prev) => (prev + 1) % heroMovies.length);
  }, []);

  const prev = useCallback(() => {
    setCurrent((prev) => (prev - 1 + heroMovies.length) % heroMovies.length);
  }, []);

  useEffect(() => {
    const timer = setInterval(next, 5000);
    return () => clearInterval(timer);
  }, [next]);

  const movie = heroMovies[current];

  return (
    <section className="relative w-full h-[500px] md:h-[620px] overflow-hidden">
      <div className="absolute inset-0">
        <img
          src={movie.poster}
          alt={movie.title}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-background-950/95 via-background-950/60 to-background-950/40"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-background-100 via-transparent to-transparent"></div>
      </div>

      <div className="container-main relative z-10 h-full flex items-center">
        <div className="w-full max-w-2xl animate-fade-in" key={current}>
          <div className="flex items-center gap-2 mb-3">
            <span className="px-2 py-0.5 text-xs font-medium bg-accent-500 text-background-50 rounded-full">
              {movie.category}
            </span>
            <span className="text-xs text-foreground-400">{movie.year}</span>
            <span className="text-xs text-foreground-400">{movie.duration}</span>
          </div>
          <h1 className="text-3xl md:text-5xl font-bold text-foreground-50 mb-3">
            {movie.title}
          </h1>
          <h2 className="text-lg md:text-xl text-foreground-400 mb-2">
            {movie.titleEn}
          </h2>
          <p className="text-sm md:text-base text-foreground-500 leading-relaxed mb-2 line-clamp-3">
            {movie.description}
          </p>
          <div className="flex items-center gap-3 mb-5">
            <span className="flex items-center gap-1 text-sm text-primary-400">
              <i className="ri-star-fill text-sm w-4 h-4 flex items-center justify-center"></i>
              {movie.rating}
            </span>
            <span className="text-xs text-foreground-600">导演: {movie.director}</span>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <Link
              to={`/article/${movie.slug}`}
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-950 font-semibold text-sm rounded-full transition-all duration-300 whitespace-nowrap"
            >
              <i className="ri-play-fill text-lg w-5 h-5 flex items-center justify-center"></i>
              立即观看
            </Link>
            <Link
              to={`/category/${movie.categorySlug}`}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-background-300/40 hover:bg-background-300/60 text-foreground-200 text-sm rounded-full transition-all duration-300 whitespace-nowrap"
            >
              更多{movie.category}
            </Link>
          </div>
        </div>
      </div>

      <button
        onClick={prev}
        className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 rounded-full bg-background-300/40 hover:bg-background-300/60 flex items-center justify-center text-foreground-200 transition-all duration-300 cursor-pointer"
        aria-label="上一张"
      >
        <i className="ri-arrow-left-s-line text-xl"></i>
      </button>
      <button
        onClick={next}
        className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 rounded-full bg-background-300/40 hover:bg-background-300/60 flex items-center justify-center text-foreground-200 transition-all duration-300 cursor-pointer"
        aria-label="下一张"
      >
        <i className="ri-arrow-right-s-line text-xl"></i>
      </button>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2">
        {heroMovies.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrent(index)}
            className={`w-2 h-2 rounded-full transition-all duration-300 cursor-pointer ${
              index === current
                ? 'w-6 bg-primary-500'
                : 'bg-background-400 hover:bg-background-500'
            }`}
            aria-label={`切换到第${index + 1}张`}
          />
        ))}
      </div>
    </section>
  );
}